import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import { css } from 'twin.macro';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { emailDraftService } from '@/_services/api/emailIntegration/emailDraft';
import { emailIntegrationService } from '@/_services/api/emailIntegration';
import EmailDraftsList from './EmailDraftsList';
import EmailComposer from './EmailComposer';
import { VscLoading } from 'react-icons/vsc';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const headerRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
`;

const modalBodyStyle = css`
    min-height: 80vh;
`;

const formatAccountLabel = (account) => {
    if (!account) return <span className="badge badge-warning">No account connected</span>;
    return <span className="badge badge-info">{account?.email} </span>;
};

const Component = ({ show, onClose, chatroomId }) => {
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerKey, setComposerKey] = useState(0);
    const [connectedAccount, setConnectedAccount] = useState(null);

    const sortedDrafts = useMemo(
        () => [...drafts].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()),
        [drafts]
    );

    const fetchDrafts = async () => {
        if (!chatroomId) return;
        try {
            setLoading(true);
            const res = await emailDraftService.getChatroomEmailDrafts({
                chatroomId,
                status: 'draft',
            });
            setDrafts(res?.results || []);
        } catch (error) {
            toast.error('Failed to load email drafts');
        } finally {
            setLoading(false);
        }
    };

    const fetchConnectedAccount = async () => {
        if (!chatroomId) return;

        try {
            const response = await emailIntegrationService.getEmailAccounts();
            const accounts = response?.results || [];
            if (Array.isArray(accounts) && accounts.length) {
                setConnectedAccount(accounts[0]);
            }
        } catch (error) {
            if (error?.name !== 'AbortError') {
                setConnectedAccount(null);
            }
        }
    };

    useEffect(() => {
        if (!show) return;
        fetchDrafts();
        fetchConnectedAccount();
        setSelectedDraft(null);
        setComposerOpen(false);
    }, [show, chatroomId]);

    const handleCreate = async () => {
        try {
            setIsCreating(true);
            const res = await emailDraftService.generateChatroomEmailDraft({
                chatroomId,
                payload: { topic: '', broadcast: false },
            });
            const draft = res?.draft || res;
            setDrafts((prev) => [draft, ...prev]);
            setSelectedDraft(draft);
            setComposerKey((prev) => prev + 1);
            setComposerOpen(true);
        } catch (error) {
            toast.error(error?.message || 'Failed to create draft');
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenDraft = (draft) => {
        setSelectedDraft(draft);
        setComposerKey((prev) => prev + 1);
        setComposerOpen(true);
    };

    const handleDeleteDraft = async (draft) => {
        const result = await Swal.fire({
            title: 'Delete draft?',
            text: `Delete "${draft?.subject || '(no subject)'}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;
        try {
            await emailDraftService.deleteChatroomEmailDraft(draft?.id || draft?._id, {
                chatroomId,
            });
            setDrafts((prev) => prev.filter((item) => item?._id !== draft?._id));
            if (selectedDraft && selectedDraft?._id === draft?._id) {
                setSelectedDraft(null);
                setComposerOpen(false);
            }
            toast.success('Draft deleted');
        } catch (error) {
            toast.error(error?.message || 'Failed to delete draft');
        }
    };

    return (
        <>
            <Modal show={show} onHide={() => onClose?.(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <div css={headerRow}>
                        <Modal.Title className="mb-0">
                            <div>
                                <div>Email Drafts</div>
                                <div>{formatAccountLabel(connectedAccount)}</div>
                            </div>
                        </Modal.Title>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={fetchDrafts}
                                disabled={loading || isCreating}
                            >
                                <FiRefreshCw className={`me-2 ${loading ? 'spin' : ''}`} />
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleCreate}
                                disabled={loading || isCreating}
                                className="ml-2"
                            >
                                {isCreating ? <VscLoading className="me-2 spin" /> : <FiPlus className="me-2" />}
                                Compose
                            </Button>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body css={modalBodyStyle}>
                    <EmailDraftsList
                        drafts={sortedDrafts}
                        loading={loading}
                        onOpenDraft={handleOpenDraft}
                        onDeleteDraft={handleDeleteDraft}
                    />
                </Modal.Body>
            </Modal>

            <EmailComposer
                key={composerKey}
                show={composerOpen}
                chatroomId={chatroomId}
                draftId={selectedDraft?.id || selectedDraft?._id}
                initialDraft={selectedDraft}
                onDeleteDraft={handleDeleteDraft}
                onClose={() => {
                    setComposerOpen(false);
                    fetchDrafts();
                }}
                onCreated={(draft) => {
                    if (draft) {
                        setDrafts((prev) => {
                            const next = prev.filter(
                                (item) => String(item._id || item.id) !== String(draft._id || draft.id)
                            );
                            return [draft, ...next];
                        });
                    }
                }}
            />
        </>
    );
};

const EmailCommandModal = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default EmailCommandModal;
